import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { zoomSliderToFactor } from '@/lib/imagen';

/** Editor de encuadre INTERACTIVO del motor central de edición de imagen
 *  (Riki 2026-07-13). Arrastra para mover, rueda/pellizco para hacer zoom,
 *  sobre un recuadro con el ratio destino (width:height). No recorta ni sube
 *  nada: solo emite el encuadre (offX/offY 0-100 + zoom slider 0-100) que el
 *  padre pasa a coverCrop(). Antes vivía embebido en Editor.tsx como
 *  FotoPositioner; se extrajo para reutilizarlo en todas las herramientas que
 *  suban una foto (Podios ahora; AthleteShareModal de Rankings después). */

const _clamp = (v: number) => Math.max(0, Math.min(100, v));

export interface ImageEditorProps {
  /** dataURL de la foto (idealmente ya pasada por optimizeImage). */
  imageUrl: string;
  /** Dimensiones del recuadro destino (definen el ratio del encuadre). */
  width: number;
  height: number;
  /** Encuadre horizontal/vertical, 0-100 (equivale a object-position %). */
  offX: number;
  offY: number;
  /** Zoom como slider 0-100 (0 = ×1 cover base, 100 = ×1.5). */
  zoom: number;
  setOffX: Dispatch<SetStateAction<number>>;
  setOffY: Dispatch<SetStateAction<number>>;
  setZoom: Dispatch<SetStateAction<number>>;
}

export function ImageEditor({
  imageUrl, width: w, height: h, offX, offY, zoom,
  setOffX, setOffY, setZoom,
}: ImageEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastXY = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(zoom);
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const [active, setActive] = useState(false);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // Arrastre con ratón
  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    setActive(true);
    lastXY.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const cW = containerRef.current.clientWidth;
      const cH = containerRef.current.clientHeight;
      const zf = zoomSliderToFactor(zoomRef.current);
      const dx = e.clientX - lastXY.current.x;
      const dy = e.clientY - lastXY.current.y;
      lastXY.current = { x: e.clientX, y: e.clientY };
      setOffX(v => _clamp(v - dx * 100 / cW / zf));
      setOffY(v => _clamp(v - dy * 100 / cH / zf));
    };
    const onUp = () => { dragging.current = false; setActive(false); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [setOffX, setOffY]);

  // Rueda del ratón (listener no-pasivo para poder hacer preventDefault)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(v => _clamp(v + (e.deltaY > 0 ? -4 : 4)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setZoom]);

  // Touch: arrastre (1 dedo) y pellizco (2 dedos)
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      dragging.current = true;
      setActive(true);
      lastXY.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      pinchRef.current = null;
    } else if (e.touches.length === 2) {
      dragging.current = false;
      setActive(false);
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      pinchRef.current = { dist: d, zoom: zoomRef.current };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    if (e.touches.length === 1 && dragging.current) {
      const cW = containerRef.current.clientWidth;
      const cH = containerRef.current.clientHeight;
      const zf = zoomSliderToFactor(zoomRef.current);
      const dx = e.touches[0].clientX - lastXY.current.x;
      const dy = e.touches[0].clientY - lastXY.current.y;
      lastXY.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setOffX(v => _clamp(v - dx * 100 / cW / zf));
      setOffY(v => _clamp(v - dy * 100 / cH / zf));
    } else if (e.touches.length === 2 && pinchRef.current) {
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      const scale = d / pinchRef.current.dist;
      const newZf = scale * zoomSliderToFactor(pinchRef.current.zoom);
      setZoom(_clamp((newZf - 1) * 200));
    }
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      dragging.current = false;
      pinchRef.current = null;
      setActive(false);
    } else if (e.touches.length === 1 && pinchRef.current) {
      // Transición de pellizco a arrastre de 1 dedo
      pinchRef.current = null;
      dragging.current = true;
      setActive(true);
      lastXY.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const zf = zoomSliderToFactor(zoom);

  return (
    <div
      ref={containerRef}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: h > w ? 380 : undefined,
        aspectRatio: `${w} / ${h}`,
        overflow: 'hidden',
        borderRadius: 'var(--ld-radius-md)',
        border: '1px solid var(--ld-line)',
        cursor: active ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <img
        src={imageUrl}
        draggable={false}
        alt=""
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: `${offX}% ${offY}%`,
          transform: `scale(${zf})`,
          transformOrigin: `${offX}% ${offY}%`,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      <div style={{
        position: 'absolute', bottom: 8, left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.5)', color: '#fff',
        borderRadius: 20, padding: '3px 10px',
        fontSize: 10, letterSpacing: '0.06em',
        whiteSpace: 'nowrap', pointerEvents: 'none',
        fontFamily: 'var(--ld-font-mono, monospace)',
      }}>
        ×{zf.toFixed(2)}
      </div>
    </div>
  );
}
